import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../../components/DataTable";
import Alert from "../../components/Alert";
import { createLookupApi } from "../../api/lookupApi";
import { listExamConfigs, saveExamConfig, deleteExamConfig } from "../../api/examApi";
import "../Users/UsersPage.css";
import "../Attendance/Attendance.css";

const classApi = createLookupApi("classes");
const examApi = createLookupApi("exams");

/** Links a class to an exam (e.g. "Class 9" + "1st Term") — the anchor every subject/mark/result setup hangs off. */
export default function ExamConfigPage() {
  const [classes, setClasses] = useState([]);
  const [exams, setExams] = useState([]);
  const [classId, setClassId] = useState("");
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState({ examId: "", examSerial: 1, percentage: 100, allowFraction: 0 });

  useEffect(() => {
    Promise.all([classApi.list(), examApi.list()]).then(([classList, examList]) => {
      setClasses(classList);
      setExams(examList);
      if (classList.length > 0) setClassId(String(classList[0].id));
    });
  }, []);

  const load = () => {
    if (!classId) return;
    setLoading(true);
    listExamConfigs(classId)
      .then(setConfigs)
      .catch(() => setError("Failed to load exam configurations."))
      .finally(() => setLoading(false));
  };

  useEffect(load, [classId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await saveExamConfig({ classId: Number(classId), examId: Number(form.examId), examSerial: Number(form.examSerial), percentage: Number(form.percentage), allowFraction: Number(form.allowFraction) });
      setSuccess("Exam configuration saved.");
      load();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to save.");
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete exam configuration "${row.examName}"?`)) return;
    setError("");
    try {
      await deleteExamConfig(row.examConfigId);
      setSuccess("Deleted.");
      setConfigs((prev) => prev.filter((c) => c.examConfigId !== row.examConfigId));
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to delete.");
    }
  };

  const columns = [
    { key: "examName", label: "Exam" },
    { key: "examSerial", label: "Serial" },
    { key: "percentage", label: "Weight %" },
    { key: "allowFraction", label: "Fraction Digits" },
  ];

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Exam Setup</h1>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      <div className="attendance-filters">
        <label>
          Class
          <select value={classId} onChange={(e) => setClassId(e.target.value)}>
            {classes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <form onSubmit={handleSubmit} className="user-form" style={{ marginTop: 16, marginBottom: 24 }}>
        <label>Exam *</label>
        <select value={form.examId} onChange={(e) => setForm({ ...form, examId: e.target.value })} required>
          <option value="" disabled>
            Select an exam
          </option>
          {exams.map((ex) => (
            <option key={ex.id} value={ex.id}>
              {ex.name}
            </option>
          ))}
        </select>
        <label>Serial</label>
        <input type="number" value={form.examSerial} onChange={(e) => setForm({ ...form, examSerial: e.target.value })} />
        <label>Weight % (toward grand final)</label>
        <input type="number" value={form.percentage} onChange={(e) => setForm({ ...form, percentage: e.target.value })} />
        <label>Fraction Digits (0 = whole marks)</label>
        <input type="number" min="0" max="3" value={form.allowFraction} onChange={(e) => setForm({ ...form, allowFraction: e.target.value })} />
        <div className="form-actions">
          <button type="submit" className="btn-primary">
            Save
          </button>
        </div>
      </form>

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          columns={columns}
          rows={configs}
          actions={(row) => (
            <button type="button" className="btn-link danger" onClick={() => handleDelete(row)}>
              Delete
            </button>
          )}
        />
      )}
    </MainLayout>
  );
}
